import apiClient from './apiClient';
import { UserPerformance } from '../types';

export interface DashboardData {
  completedQuizzes: number;
  averageScore: number;
  currentStreak: number;
  weeklyProgress: any[];
  trends?: {
    completedQuizzesTrend: { value: number; isPositive: boolean };
    averageScoreTrend: { value: number; isPositive: boolean };
    streakTrend: { value: number; isPositive: boolean };
  };
  subjectPerformances: any[];
  strandPerformances: Array<{strandId: string, strandName: string, averageScore: number, totalQuizzes: number}>;
  substrandPerformances: Array<{substrandId: string, substrandName: string, strandId: string, averageScore: number, totalQuizzes: number}>;
  contentPointPerformances: Array<{contentPointId: string, contentDescription: string, averageScore: number, totalQuizzes: number}>;
}

/**
 * Fetches dashboard data for a student (requires authentication)
 * @param userId The ID of the user
 * @returns A Promise that resolves to UserPerformance with trends
 */
export const getDashboardData = async (userId: string): Promise<UserPerformance & { 
  trends?: DashboardData['trends']; 
  currentStreak?: number;
  realStrandPerformances?: DashboardData['strandPerformances'];
  substrandPerformances?: DashboardData['substrandPerformances'];
  contentPointPerformances?: DashboardData['contentPointPerformances'];
}> => {
  try {
    const response = await apiClient.get<DashboardData>(`/dashboard/student/${userId}`);
    
    // Convert DashboardData to UserPerformance format, including hierarchical performance data
    const userPerformance: UserPerformance & { 
      trends?: DashboardData['trends']; 
      currentStreak?: number;
      realStrandPerformances?: DashboardData['strandPerformances'];
      substrandPerformances?: DashboardData['substrandPerformances'];
      contentPointPerformances?: DashboardData['contentPointPerformances'];
    } = {
      userId: parseInt(userId),
      strandPerformances: [], // Keep legacy format for compatibility
      overallPercentage: response.data.averageScore || 0,
      totalQuizAttempts: response.data.completedQuizzes || 0,
      lastActivityDate: new Date(), // Default to current date
      trends: response.data.trends,
      currentStreak: response.data.currentStreak || 0,
      // Add new performance data
      realStrandPerformances: response.data.strandPerformances || [],
      substrandPerformances: response.data.substrandPerformances || [],
      contentPointPerformances: response.data.contentPointPerformances || []
    } as any; // Use any to bypass type checking for now since we're extending the interface
    
    return userPerformance;
  } catch (error) {
    console.error(`Error fetching dashboard data for user ${userId}:`, error);
    throw error;
  }
};